/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { MyInjector } from '@src/app/app.module';
import { Txn } from '@src/app/model/txn';
import { PurchaseService } from '@src/app/modules/txn/purchase.service';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { PreferredSupplier } from '@src/app/model/supplier';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { PurchasePageComponent } from '@src/app/modules/txn/purchase-page/purchase-page.component';
import { of } from 'rxjs';

export class WriteDownAccrualHelper {

    purchaseSvc = MyInjector.instance.get(PurchaseService);
    dialog = MyInjector.instance.get(MatDialog);

    constructor( private accruals: Txn[], private supplier: PreferredSupplier) {

    }

    checkAllocation = (o: Txn): ValidatorFn => (ctl: AbstractControl): ValidationErrors => {
        if (ctl.value > Math.abs(o.outstanding)) {
            return {overPay: $localize `Overallocated`}
        }
        return null;
    }


    getAccrualPaymentGrid() {
        return new GridField({
            field:
                { label: 'Allocations', value: 'accruals'},
            rowFactory: (o: Txn) => {
                const amtField = FormNumberComponent.makeCurrency('Allocate','allocate', {
                    validators:[this.checkAllocation(o)],
                    calculateValue: (o: Txn) => Math.abs(o.outstanding)
                });
                amtField.numberOpts.width = 6;

                const payer = () => {
                    const avail = Math.abs(o?.outstanding);
                    amtField.control.setValue(avail, {emitEvent: true});
                    amtField.control.updateValueAndValidity()
                };

                const payBtn = FormButtonComponent.makeIconButton('allocateIt', 'check_circle', $localize `Allocate to this invoice`, payer);
                payBtn.cellOpts.style = 'padding-left: 0px; padding-right: 0px';

                const clearer = () => amtField.control.setValue(0);
                const clearBtn = FormButtonComponent.makeIconButton('clearIt', 'cancel', $localize `Clear allocation`, clearer);
                clearBtn.cellOpts.style = 'padding-left: 0px; padding-right: 0px';
                clearBtn.btnOpts.color = 'warn';

                return [
                    FieldMaker.idHolder('id'),
                    FormButtonComponent.makeTextButton('Item', 'refNr', (c) => this.showItem(o,c), { readonly: true}),                    
                    FormTextComponent.make('Ref', 'reference', {readonly: true}),
                    FormDateComponent.make('Date', 'txnDate', {readonly: true}),
                    FormNumberComponent.makeCurrency('Total','credit', {readonly: true, calculateValue: (o: Txn) => Math.abs(o.debit - o.credit)}),
                    FormNumberComponent.makeCurrency('Outstanding','outstanding', {readonly: true, calculateValue: (o: Txn) => Math.abs(o.outstanding)}),
                    amtField,
                    payBtn,
                    clearBtn
            ]}
        });
    }

    showItem(o: Txn, c: unknown) {
        console.log({o,c});
        const popDialogRef = this.dialog.open(PurchasePageComponent, {data: {id: o.id, forceTeamId: o.teamId}});
        popDialogRef.afterClosed().subscribe(txn => {
            if (txn) {
                Object.assign(o, txn);
            }
        })
    }

    saveAllocations(value: {accruals: Txn[]}) {
        return of(value.accruals);
    }
    getHelpText() { // Whitespace matters, goes in text area!
        return $localize `Press cancel if the item you are entering does not refer to this accrual. Press reverse if it does. If the amount is different to the accrual, you can adjust the amount by entering expense line items to increase or decrease the amount. Only reduce the amount here if you are expecting a further invoice/credit against this accrual.`
    }

    openDialog() {
        const dialog = MyInjector.instance.get(MatDialog);
        const fieldSet = new FieldSet({
            fields: [
                FormTextAreaComponent.make('', 'helpText', {readonly: true, sendServer: false}),
                this.getAccrualPaymentGrid()
            ],
            formLayout: LAYOUT_OPTIONS.singleCol
        })
        fieldSet.setValue({accruals: this.accruals, helpText: this.getHelpText()} as unknown as Txn)
        const data = {
            fieldSet,
            saveFn: this.saveAllocations.bind(this),
            saveText: $localize `Reverse`,
            title: $localize `Outstanding Accruals for ${this.supplier.name}`,
            minWidth: '700px',
        } as FieldSetDialogOptions

        return dialog.open(FieldSetDialogComponent, {data} ).afterClosed();
    }
}
